/* ==========================================================================
   NAD+ Liquid product template — shared behaviour.
   One observer drives every .nad-reveal; sections register their own
   custom elements. Namespaced so nothing else on the theme is touched.
   ========================================================================== */
(function () {
  "use strict";

  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  window.NadReduceMotion = reduce;

  function initReveal() {
    var els = document.querySelectorAll(".nad-reveal:not(.nad-in)");
    if (!els.length) return;

    if (reduce || !("IntersectionObserver" in window)) {
      els.forEach(function (el) {
        el.classList.add("nad-in");
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.classList.add("nad-in");
          io.unobserve(e.target);
        });
      },
      { threshold: 0.08 }
    );

    els.forEach(function (el) {
      io.observe(el);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initReveal);
  } else {
    initReveal();
  }

  document.addEventListener("shopify:section:load", initReveal);

  /* ------------------------------------------------------------------
     <nad-accordion> — used by the ingredients, FAQ and references
     sections. Add the `single` attribute to close siblings on open,
     which is how the reference ingredient and FAQ lists behave; the
     references list toggles independently.
     ------------------------------------------------------------------ */
  if (!customElements.get("nad-accordion")) {
    customElements.define(
      "nad-accordion",
      class NadAccordion extends HTMLElement {
        connectedCallback() {
          this.single = this.hasAttribute("single");
          this.addEventListener("click", this.onClick.bind(this));
        }

        onClick(event) {
          var head = event.target.closest(".nad-acc-head");
          if (!head || !this.contains(head)) return;

          var item = head.closest(".nad-acc-item");
          if (!item) return;

          var willOpen = !item.classList.contains("is-open");

          if (this.single) {
            this.querySelectorAll(".nad-acc-item.is-open").forEach(
              function (other) {
                if (other !== item) this.setOpen(other, false);
              }.bind(this)
            );
          }

          this.setOpen(item, willOpen);
        }

        setOpen(item, open) {
          item.classList.toggle("is-open", open);
          var head = item.querySelector(".nad-acc-head");
          if (head) head.setAttribute("aria-expanded", open ? "true" : "false");
          var panel = item.querySelector(".nad-acc-panel");
          if (panel) panel.hidden = !open;
        }
      }
    );
  }

  /* ------------------------------------------------------------------
     <nad-carousel> — the testimonial, video and timeline sliders, plus
     the myth cards on mobile. A scroll-snapped track with arrows and
     page dots; slide widths come from each section's own CSS.
     Add `desktop-static` to disable it above 750px, which is how the
     reference myth cards behave.
     ------------------------------------------------------------------ */
  if (!customElements.get("nad-carousel")) {
    customElements.define(
      "nad-carousel",
      class NadCarousel extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector(".nad-car-track");
          this.dotsEl = this.querySelector(".nad-car-dots");
          if (!this.track) return;

          this.querySelectorAll(".nad-car-arrow").forEach(
            function (btn) {
              btn.addEventListener(
                "click",
                function () {
                  this.page(Number(btn.dataset.dir) || 1);
                }.bind(this)
              );
            }.bind(this)
          );

          this.sync = this.sync.bind(this);
          this.track.addEventListener("scroll", this.sync, { passive: true });
          window.addEventListener("resize", this.sync);
          this.sync();
        }

        disconnectedCallback() {
          window.removeEventListener("resize", this.sync);
        }

        /* Paging is by slide, not by viewport, so the dot count matches the
           reference: ceil(slides / slides-per-view). */
        get metrics() {
          var slides = this.track.querySelectorAll(".nad-car-slide");
          if (!slides.length) return { pages: 1, step: 1, count: 0 };

          var width = slides[0].getBoundingClientRect().width;
          var gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
          var stride = width + gap;
          var perView = Math.max(
            1,
            Math.round((this.track.clientWidth + gap) / stride)
          );

          return {
            pages: Math.max(1, Math.ceil(slides.length / perView)),
            step: perView * stride,
            count: slides.length,
          };
        }

        get current() {
          var step = this.metrics.step;
          return step ? Math.round(this.track.scrollLeft / step) : 0;
        }

        page(dir) {
          this.goTo(this.current + dir);
        }

        goTo(index) {
          var m = this.metrics;
          var next = Math.min(Math.max(index, 0), m.pages - 1);
          this.track.scrollTo({
            left: next * m.step,
            behavior: window.NadReduceMotion ? "auto" : "smooth",
          });
        }

        sync() {
          var m = this.metrics;
          var pages = m.pages;
          var current = m.step
            ? Math.round(this.track.scrollLeft / m.step)
            : 0;

          if (this.dotsEl) {
            if (this.dotsEl.children.length !== pages) {
              this.dotsEl.textContent = "";
              for (var i = 0; i < pages; i++) {
                var dot = document.createElement("button");
                dot.type = "button";
                dot.className = "nad-car-dot";
                dot.setAttribute("aria-label", "Go to slide " + (i + 1));
                dot.dataset.index = i;
                dot.addEventListener(
                  "click",
                  function (e) {
                    this.track.scrollTo({
                      left: Number(e.currentTarget.dataset.index) * this.metrics.step,
                      behavior: window.NadReduceMotion ? "auto" : "smooth",
                    });
                  }.bind(this)
                );
                this.dotsEl.appendChild(dot);
              }
            }
            [].forEach.call(this.dotsEl.children, function (dot, i) {
              dot.classList.toggle("is-active", i === current);
            });
          }

          this.querySelectorAll(".nad-car-arrow").forEach(function (btn) {
            var dir = Number(btn.dataset.dir) || 1;
            btn.disabled = dir < 0 ? current <= 0 : current >= pages - 1;
          });
        }
      }
    );
  }
})();
